import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BellRing, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { enablePush, disablePush, getSubscription, pushState } from '@/lib/push';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ id, label, hint, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between gap-3 py-1.5" data-testid={`push-row-${id}`}>
    <div className="min-w-0">
      <p className="text-sm text-slate-800">{label}</p>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={`push-toggle-${id}`} aria-label={label} />
  </div>
);

export const PushSettings = () => {
  const { t } = useTranslation();
  const { getAuthHeader } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [deviceOn, setDeviceOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const state = pushState();

  useEffect(() => {
    axios.get(`${API}/push/preferences`, { headers: { Authorization: getAuthHeader() } }).then((r) => setPrefs(r.data)).catch(() => setPrefs({ enabled: true, sos: true, orders: true, chat: true, devices: 0 }));
    getSubscription().then((s) => setDeviceOn(!!s)).catch(() => setDeviceOn(false));
  }, [getAuthHeader]);

  const savePref = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const r = await axios.put(`${API}/push/preferences`, { [key]: value }, { headers: { Authorization: getAuthHeader() } });
      setPrefs(r.data);
    } catch { toast.error(t('push.saveError')); }
  };

  const toggleDevice = async (on) => {
    setBusy(true);
    try {
      if (on) {
        await enablePush(getAuthHeader());
        setDeviceOn(true);
        await savePref('enabled', true);
        toast.success(t('push.enabled'));
      } else {
        await disablePush(getAuthHeader());
        setDeviceOn(false);
        toast.success(t('push.disabled'));
      }
    } catch (e) {
      const code = e?.message;
      toast.error(code === 'ios_install_required' ? t('push.iosInstall') : code === 'denied' ? t('push.deniedHint') : code === 'unsupported' ? t('push.unsupported') : t('push.error'));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    try {
      await axios.post(`${API}/push/test`, {}, { headers: { Authorization: getAuthHeader() } });
      toast.success(t('push.testSent'));
    } catch { toast.error(t('push.error')); }
  };

  if (!prefs) return null;
  const blocked = state === 'denied';
  return (
    <div className="space-y-2" data-testid="push-settings">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><BellRing size={16} className="text-emerald-600" /> {t('push.title')}</h3>
      <p className="text-xs text-slate-500">{t('push.desc')}</p>
      <Row id="device" label={t('push.thisDevice')} hint={blocked ? t('push.deniedHint') : state === 'ios_install_required' ? t('push.iosInstall') : state === 'unsupported' ? t('push.unsupported') : deviceOn ? t('push.deviceOn') : t('push.deviceOff')} checked={deviceOn} onChange={toggleDevice} disabled={busy || blocked || state === 'unsupported'} />
      <div className={`rounded-lg border border-slate-200 px-3 py-1 ${prefs.enabled ? '' : 'opacity-60'}`}>
        <Row id="enabled" label={t('push.all')} hint={t('push.allHint')} checked={!!prefs.enabled} onChange={(v) => savePref('enabled', v)} />
        <div className="border-t border-slate-100" />
        <Row id="sos" label={t('push.sos')} checked={!!prefs.sos} onChange={(v) => savePref('sos', v)} disabled={!prefs.enabled} />
        <Row id="orders" label={t('push.orders')} checked={!!prefs.orders} onChange={(v) => savePref('orders', v)} disabled={!prefs.enabled} />
        <Row id="chat" label={t('push.chat')} checked={!!prefs.chat} onChange={(v) => savePref('chat', v)} disabled={!prefs.enabled} />
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1"><Smartphone size={12} /> {t('push.devices', { count: prefs.devices || 0 })}</span>
        {deviceOn && <Button size="sm" variant="ghost" onClick={sendTest} data-testid="push-test-button" className="h-7 text-xs text-emerald-700">{t('push.sendTest')}</Button>}
      </div>
    </div>
  );
};

export default PushSettings;
